import * as React from 'react';
import { Loader2, RefreshCw, Unplug, Wifi, WifiOff, Crown } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Label } from '../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { useToast } from '../../../../components/ui/toast';
import { cn } from '../../../../lib/utils';
import {
  startGoogleOauthPopup,
  useDisconnectGoogle,
  useGoogleAuthStatus,
  useGoogleCalendars,
  useRegisterCalendarWatch,
  useSetActiveCalendar,
  useSyncCalendarNow,
} from '../../../../hooks/useGoogleCalendar';
import {
  startMicrosoftOauthPopup,
  useDisconnectMicrosoft,
  useMicrosoftAuthStatus,
  useMicrosoftCalendars,
  useSetActiveMicrosoftCalendar,
} from '../../../../hooks/useMicrosoftCalendar';
import {
  useCalendarPrefs,
  useSetCalendarPrefs,
  type CalendarProvider,
} from '../../../../hooks/useCalendarPrefs';
import {
  useTicketSettings,
  useUpdateTicketSettings,
} from '../../../../hooks/useTicketSettings';
import { NotificationTriggerCard } from '../../../../components/NotificationTriggerCard';
import { ZapierWebhookCard } from '../../../../components/ZapierWebhookCard';
import { BookingSlotsCard } from './BookingSlotsCard';
import {
  DEFAULT_TRIGGER_NOTIFICATION_CONFIG,
  DEFAULT_ZAPIER_WEBHOOK_CONFIG,
  type TriggerNotificationConfig,
  type ZapierWebhookConfig,
  type ZapierWebhookEntry,
} from '@cp3/shared';

export function CalendarTab() {
  const prefs = useCalendarPrefs();
  const setPrefs = useSetCalendarPrefs();
  const { toast } = useToast();

  const primary: CalendarProvider = prefs.data?.primaryProvider ?? 'google';

  async function setPrimary(provider: CalendarProvider) {
    if (provider === primary) return;
    try {
      await setPrefs.mutateAsync({ primaryProvider: provider });
      toast({
        title:
          provider === 'google'
            ? 'Google ist jetzt der primäre Kalender'
            : 'Microsoft ist jetzt der primäre Kalender',
        description: 'Neue Buchungen landen ab sofort dort.',
      });
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Primärer Kalender</CardTitle>
          <CardDescription>
            Bestimmt, wo neue Termine angelegt werden. Beide verbundenen Kalender werden
            für Verfügbarkeitsprüfungen gelesen — der Voice-Agent bucht aber nur in den
            Primär-Kalender, um Doppel-Buchungen zu vermeiden.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2">
            <PrimaryRadio
              label="Google"
              value="google"
              active={primary === 'google'}
              onClick={() => setPrimary('google')}
              disabled={setPrefs.isPending}
            />
            <PrimaryRadio
              label="Microsoft Outlook / 365"
              value="microsoft"
              active={primary === 'microsoft'}
              onClick={() => setPrimary('microsoft')}
              disabled={setPrefs.isPending}
            />
          </div>
        </CardContent>
      </Card>

      <GoogleCard isPrimary={primary === 'google'} />
      <MicrosoftCard isPrimary={primary === 'microsoft'} />

      <BookingSlotsCard />

      <AppointmentNotifyCard />
      <AppointmentZapierCard />
    </div>
  );
}

/**
 * Zapier-webhook config for the "Termin gebucht"-trigger. Lives on the
 * SAME `ticketSettings` doc as the in-house notification trigger above
 * (single-source-of-truth — see AppointmentNotifyCard for the
 * rationale).
 */
function AppointmentZapierCard() {
  const settings = useTicketSettings();
  const update = useUpdateTicketSettings();
  const { toast } = useToast();
  const cfg: ZapierWebhookConfig =
    settings.data?.zapierWebhooks ?? DEFAULT_ZAPIER_WEBHOOK_CONFIG;

  async function onChange(next: ZapierWebhookEntry) {
    try {
      await update.mutateAsync({
        zapierWebhooks: { ...cfg, appointmentBooked: next },
      });
    } catch (err) {
      toast({
        title: 'Speichern fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <ZapierWebhookCard
      title="Zapier-Webhook bei Termin-Buchung"
      description={
        'Sobald die Sprach-KI einen Termin im Kalender einträgt, schicken wir ' +
        'die Termindaten als JSON an Deinen Zap. Beispiel-Felder: Anrufer, ' +
        'Start/Ende, Termintyp, Confirmation-Code.'
      }
      trigger="appointmentBooked"
      value={cfg.appointmentBooked}
      onChange={onChange}
      saving={update.isPending}
    />
  );
}

/**
 * Renders the appointment-booked notification config — backed by the
 * SAME `ticketSettings` document the Tickets-page uses for the
 * ticket-created variant. We split the UI by trigger (calendar vs
 * tickets) but keep one settings document to avoid splitting the SLA
 * + escalation block away from its tenants.
 */
function AppointmentNotifyCard() {
  const settings = useTicketSettings();
  const update = useUpdateTicketSettings();
  const { toast } = useToast();
  const value =
    settings.data?.appointmentBookedNotify ?? DEFAULT_TRIGGER_NOTIFICATION_CONFIG;

  async function onChange(next: TriggerNotificationConfig) {
    try {
      await update.mutateAsync({ appointmentBookedNotify: next });
    } catch (err) {
      toast({
        title: 'Speichern fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <NotificationTriggerCard
      title="Benachrichtigung bei Termin-Buchung"
      description="Wird ausgelöst, sobald der Voice-Agent einen Termin im Kalender erstellt hat. Pro Kanal Vorlage + Versand-Verzögerung wählen."
      value={value}
      onChange={onChange}
    />
  );
}

function PrimaryRadio({
  label,
  value,
  active,
  onClick,
  disabled,
}: {
  label: string;
  value: CalendarProvider;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-provider={value}
      className={cn(
        'flex items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors',
        active
          ? 'border-primary bg-primary/10'
          : 'border-border hover:bg-secondary/50',
      )}
    >
      <span
        className={cn(
          'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
          active ? 'border-primary' : 'border-border',
        )}
      >
        {active && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
      </span>
      <span className="flex-1 text-sm font-medium">{label}</span>
      {active && <Crown className="h-3.5 w-3.5 text-primary" />}
    </button>
  );
}

// ── Google card ───────────────────────────────────────────────────────────

function GoogleCard({ isPrimary }: { isPrimary: boolean }) {
  const status = useGoogleAuthStatus();
  const calendars = useGoogleCalendars();
  const disconnect = useDisconnectGoogle();
  const setActive = useSetActiveCalendar();
  const sync = useSyncCalendarNow();
  const watch = useRegisterCalendarWatch();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = React.useState(false);

  const watchExpiresAt = status.data?.watchExpiresAt ?? null;
  const watchValid = !!watchExpiresAt && watchExpiresAt > Date.now();

  async function onConnect() {
    setBusy(true);
    try {
      const r = await startGoogleOauthPopup();
      if (r.ok) {
        toast({ title: 'Google Kalender verbunden' });
        await qc.invalidateQueries({ queryKey: ['google-auth-status'] });
        try {
          await watch.mutateAsync();
        } catch {
          /* user can retry from button */
        }
      } else {
        toast({ title: 'Verbindung abgebrochen', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Verbindung fehlgeschlagen', description: (err as Error).message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }

  async function onDisconnect() {
    if (!confirm('Google-Kalender-Verbindung trennen?')) return;
    try {
      await disconnect.mutateAsync();
      toast({ title: 'Getrennt' });
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }

  async function onSync() {
    try {
      const r = await sync.mutateAsync();
      toast({ title: 'Synchronisiert', description: `${r.updated} Events aktualisiert, ${r.deleted} entfernt.` });
    } catch (err) {
      toast({ title: 'Sync fehlgeschlagen', description: (err as Error).message, variant: 'destructive' });
    }
  }

  async function onChangeCalendar(id: string) {
    try {
      await setActive.mutateAsync(id);
      toast({ title: 'Kalender geändert' });
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              Google Kalender
              {isPrimary && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-primary">
                  <Crown className="h-3 w-3" /> primär
                </span>
              )}
            </CardTitle>
            <CardDescription>OAuth, aktiver Kalender, Push-Sync.</CardDescription>
          </div>
          <ProviderStatusBadge loading={status.isLoading} connected={!!status.data?.connected} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!status.data?.connected ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              Wir öffnen ein Popup mit Googles Standard-Consent-Screen. Tokens werden via KMS
              verschlüsselt in Firestore abgelegt.
            </p>
            <Button onClick={onConnect} disabled={busy} size="sm">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Mit Google verbinden
            </Button>
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
              <div className="space-y-1.5">
                <Label>Aktiver Kalender</Label>
                <Select
                  value={status.data.calendarId ?? 'primary'}
                  onValueChange={onChangeCalendar}
                  disabled={calendars.isLoading || setActive.isPending}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(calendars.data ?? []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.summary} {c.primary ? '· primary' : ''}
                      </SelectItem>
                    ))}
                    {!calendars.data && <SelectItem value="primary">Primary</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                <Button onClick={onSync} disabled={sync.isPending} variant="outline" size="sm">
                  {sync.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                  Jetzt synchronisieren
                </Button>
              </div>
            </div>

            <div className="rounded-md border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
              <div>
                Watch-Status:{' '}
                {watchValid ? (
                  <span className="text-emerald-400">
                    aktiv bis {new Date(watchExpiresAt!).toLocaleString('de-DE')}
                  </span>
                ) : (
                  <span className="text-amber-400">nicht eingerichtet</span>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="mt-2 h-7 px-2 text-xs"
                onClick={async () => {
                  try {
                    const r = await watch.mutateAsync();
                    toast({
                      title: 'Watch erneuert',
                      description: `Bis ${new Date(r.expiresAt).toLocaleDateString('de-DE')}`,
                    });
                  } catch (err) {
                    toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
                  }
                }}
                disabled={watch.isPending}
              >
                Watch erneuern
              </Button>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={onDisconnect}
                disabled={disconnect.isPending}
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
              >
                <Unplug className="mr-2 h-4 w-4" /> Trennen
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Microsoft card ────────────────────────────────────────────────────────

function MicrosoftCard({ isPrimary }: { isPrimary: boolean }) {
  const status = useMicrosoftAuthStatus();
  const calendars = useMicrosoftCalendars();
  const disconnect = useDisconnectMicrosoft();
  const setActive = useSetActiveMicrosoftCalendar();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = React.useState(false);

  async function onConnect() {
    setBusy(true);
    try {
      const r = await startMicrosoftOauthPopup();
      if (r.ok) {
        toast({ title: 'Microsoft Outlook verbunden' });
        await qc.invalidateQueries({ queryKey: ['microsoft-auth-status'] });
      } else {
        toast({ title: 'Verbindung abgebrochen', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Verbindung fehlgeschlagen', description: (err as Error).message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }

  async function onDisconnect() {
    if (!confirm('Microsoft-Verbindung trennen?')) return;
    try {
      await disconnect.mutateAsync();
      toast({ title: 'Getrennt' });
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }

  async function onChangeCalendar(id: string) {
    try {
      await setActive.mutateAsync(id);
      toast({ title: 'Kalender geändert' });
    } catch (err) {
      toast({ title: 'Fehler', description: (err as Error).message, variant: 'destructive' });
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              Microsoft Outlook / 365
              {isPrimary && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-primary">
                  <Crown className="h-3 w-3" /> primär
                </span>
              )}
            </CardTitle>
            <CardDescription>
              OAuth via Microsoft Identity Platform, Calendar.ReadWrite + offline_access.
            </CardDescription>
          </div>
          <ProviderStatusBadge loading={status.isLoading} connected={!!status.data?.connected} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!status.data?.connected ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              Wir öffnen ein Popup mit Microsofts Consent-Screen. Funktioniert mit
              persönlichen Microsoft-Konten und Work/School-Konten (Office 365).
              Tokens werden via KMS verschlüsselt in Firestore abgelegt.
            </p>
            <Button onClick={onConnect} disabled={busy} size="sm">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Mit Microsoft verbinden
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label>Aktiver Kalender</Label>
              <Select
                value={status.data.calendarId ?? 'primary'}
                onValueChange={onChangeCalendar}
                disabled={calendars.isLoading || setActive.isPending}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(calendars.data ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.summary} {c.primary ? '· default' : ''}
                    </SelectItem>
                  ))}
                  {!calendars.data && <SelectItem value="primary">Default</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={onDisconnect}
                disabled={disconnect.isPending}
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
              >
                <Unplug className="mr-2 h-4 w-4" /> Trennen
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ProviderStatusBadge({ loading, connected }: { loading: boolean; connected: boolean }) {
  if (loading) return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  return connected ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-400">
      <Wifi className="h-3 w-3" /> verbunden
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs text-amber-400">
      <WifiOff className="h-3 w-3" /> nicht verbunden
    </span>
  );
}
